import {useCallback, useEffect, useMemo, useState} from 'react';

import {addMinutes, addHours, addDays, addBusinessDays} from 'date-fns';
import _ from 'lodash';
import {useAppSelector} from 'Store/Store';

import {DAY_MINUTES, DURATION_TYPE, MAX_CUSHION} from './consts';

import {toDateTime} from 'Components/BookingBlocksWidget/utils';
import {getCushionTime} from 'Utils/apptAvailabilityUtils';

const defaultShouldRefresh = () => true;

export const getDayBoundries = (timeZone) => {
  return _.range(Math.ceil(MAX_CUSHION/DAY_MINUTES) + 1).map((val) => (
    toDateTime(undefined, timeZone).startOf('day').plus({days: val}).toJSDate()
  ));
};

/**
 * Custom hook to manage time-dependent refresh logic
 * @param {Object} options
 * @param {?Number} options.refreshInterval set an interval for periodic date refresh in ms
 * @param {?Number} options.refreshTimeout set a timeout for the next date refresh
 * @param {?Boolean} options.refreshOnFocus whether or not to refresh on window focus
 * @param {?Function} options.shouldRefresh function to decide whether latest Date value should cause a refresh
 * @return {Date}
 */
export function useDateRefresher(options={
  refreshInterval: undefined,
  refreshOnFocus: false,
  refreshTimeout: undefined,
  shouldRefresh: defaultShouldRefresh,
}) {
  const refreshInterval = options?.refreshInterval;
  const refreshOnFocus = options?.refreshOnFocus;
  const refreshTimeout = options?.refreshTimeout;
  const shouldRefresh = options?.shouldRefresh || defaultShouldRefresh;

  const [now, setNow] = useState(new Date());

  const updateNow = useCallback(() => {
    const now = new Date();
    if (shouldRefresh(now)) {
      setNow(now);
    }
  }, [shouldRefresh]);

  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(updateNow, refreshInterval);
      return () => {
        clearInterval(interval);
      };
    }
  }, [refreshInterval, updateNow]);

  useEffect(() => {
    if (refreshTimeout) {
      const interval = setTimeout(updateNow, refreshTimeout);
      return () => {
        clearTimeout(interval);
      };
    }
  }, [refreshTimeout, updateNow]);

  useEffect(() => {
    if (refreshOnFocus) {
      window.addEventListener('focus', updateNow);
      return () => {
        window.removeEventListener('focus', updateNow);
      };
    }
  }, [refreshOnFocus, updateNow]);

  return now;
}

const getCutOff = (appt, date = new Date()) => {
  let cutoff;
  if (appt?.minBookingTimeUnit) { // for latest v3 cushion time format
    switch (appt.minBookingTimeUnit) {
      case DURATION_TYPE.HOURS:
        cutoff = addHours(date, appt.minBookingTime);
        break;
      case DURATION_TYPE.DAYS:
        cutoff = addDays(date, appt.minBookingTime);
        break;
      case DURATION_TYPE.BUSINESS_DAYS:
        cutoff = addBusinessDays(date, appt.minBookingTime);
        break;
      default: // default to DURATION_TYPE.MINUTES
        cutoff = addMinutes(date, appt.minBookingTime);
    }
  } else { // for v1 and v2 cushion time format
    const cushionMinutes = getCushionTime(appt);
    cutoff = addMinutes(date, cushionMinutes);
  }
  return cutoff;
};

/**
 * custom hook to encapsulate time dependent refreshing logic and get the
 * first slot out of `availableTimes` that obeys the cushion cutoff parameter.
 *
 * refreshes returned value every time the next available slot fails the cushion requirement
 * @param {?Appt} [appt]
 * @param {Array<Date>} availableTimes array of all non-busy times for a given day
 * @return {Date} the current cushion cutoff
 */
export function useCushionCutoff(appt, availableTimes) {
  const cutoff = getCutOff(appt);
  const nextOpening = availableTimes.find((time) => cutoff < time);

  // Configures to refresh date when:
  // - next available time fails the cushion requirement
  // - window is re-focused
  const refreshParameters = useMemo(() => {
    // if there is no next opening, don't set a refresh timer
    const nextRefreshTimeout = nextOpening ? nextOpening - cutoff : undefined;
    return ({
      refreshOnFocus: true,
      refreshTimeout: nextRefreshTimeout,
      shouldRefresh: (currentTime) => {
        const nextCutoff = getCutOff(appt, currentTime);
        if (nextOpening) {
          return nextCutoff > nextOpening;
        }
        return true;
      },
    });
  }, [nextOpening, cutoff, appt]);

  const lastRefreshDate = useDateRefresher(refreshParameters);

  const cushionCutoff = useMemo(() => getCutOff(appt, lastRefreshDate), [appt, lastRefreshDate]);
  return cushionCutoff;
}

/**
 * Hook to return an array of dates starting with the selectedDate
 *  The selectedDate will be interpreted in the local system timeZone
 *
 *  Each Date in the returned array will be the start of the day in the given timeZone.
 * @param {Date} selectedDate
 * @param {number} numberOfDateColumns
 * @param {string} timeZone
 * @return {Date[]}
 */
export const useDisplayDatesWindows = (selectedDate, numberOfDateColumns, timeZone) => {
  // true: 'Work week' toggle button off, show all days
  // false: 'Work week' toggle button on, show weekdays only
  const weekendStatus = useAppSelector((state) => state.hostSettingsState?.weekendStatus);

  const datesWindow = useMemo(() => {
    if (!selectedDate || numberOfDateColumns <= 0) return [];

    const result = [];
    let count = 0;
    let offset = 0;

    while (count < numberOfDateColumns) {
      const currentDate = addDays(selectedDate, offset);
      // Check if the current date is a weekday (Monday to Friday) in the appointment's timezone
      // Use Luxon's toDateTime to properly handle timezone conversion
      // Luxon weekday: 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday, 7=Sunday
      const dateInTimezone = toDateTime(currentDate, timeZone, true);
      const isWeekday = dateInTimezone.weekday >= 1 && dateInTimezone.weekday <= 5;
      const shouldIncludeDate = isWeekday || weekendStatus;

      if (shouldIncludeDate) {
        result.push(currentDate);
        count++;
      }
      offset++;
    }
    return result;
  }, [selectedDate, numberOfDateColumns, weekendStatus, timeZone]);

  return datesWindow;
};

